import { getClientErrorTranslations } from '@common/i18n/clientError';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { Box, Button, Typography } from '@mui/material';
import ClientViewWrapper from './ClientViewWrapper';
import FormContainer from './FormContainer';
import { mainWrapperStyles } from './styles';

interface Props {
    message?: string;
    onRetry?: () => void;
    isRetrying?: boolean;
    language?: TemplateFormLanguage;
}

function ConnectivityTips({ language }: { language: TemplateFormLanguage }) {
    const t = getClientErrorTranslations(language);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#555' }}>
                {t.connectivityIntro}
            </Typography>
            <Typography
                variant="body2"
                component="ul"
                sx={{
                    color: '#555',
                    pl: 2.5,
                    m: 0,
                    '& li': { mb: 0.75 },
                    '& li:last-of-type': { mb: 0 },
                }}
            >
                <li>
                    {t.connectivityRetryTipPrefix}
                    <strong>{t.retry}</strong>
                    {t.connectivityRetryTipSuffix}
                </li>
                <li>{t.connectivityDesktopTip}</li>
                <li>{t.connectivityContactTip}</li>
            </Typography>
        </Box>
    );
}

export default function ClientErrorPage({
    message,
    onRetry,
    isRetrying,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const t = getClientErrorTranslations(language);
    const isRetryable = !!onRetry;
    const headline = message ?? (isRetryable ? t.retryableHeadline : t.terminalHeadline);

    return (
        <ClientViewWrapper>
            <Box
                sx={{
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box sx={mainWrapperStyles}>
                    <FormContainer>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 2,
                            }}
                        >
                            <Typography variant="body1" sx={{ color: '#333' }}>
                                {headline}
                            </Typography>
                            {isRetryable ? <ConnectivityTips language={language} /> : null}
                            {!isRetryable && !message ? (
                                <Typography variant="body2" sx={{ color: '#555' }}>
                                    {t.terminalAccessMessage}
                                </Typography>
                            ) : null}
                            {onRetry ? (
                                <Button variant="outlined" onClick={onRetry} disabled={isRetrying}>
                                    {t.retry}
                                </Button>
                            ) : null}
                        </Box>
                    </FormContainer>
                </Box>
            </Box>
        </ClientViewWrapper>
    );
}
